import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import { Toast, type ToastMessage, type ToastRef } from "@uigovpe/components";

type ToastFeedbackOptions = Omit<
  ToastMessage,
  "detail" | "severity" | "summary"
> & {
  summary?: string;
};

type ToastContextValue = {
  showErrorToast: (detail: string, options?: ToastFeedbackOptions) => void;
  showSuccessToast: (detail: string, options?: ToastFeedbackOptions) => void;
  showToast: (message: ToastMessage) => void;
};

type ToastProviderProps = {
  children: ReactNode;
};

const DEFAULT_TOAST_LIFE = 5000;

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: ToastProviderProps) {
  const toastRef = useRef<ToastRef>(null);

  const showToast = useCallback((message: ToastMessage) => {
    toastRef.current?.show({
      life: DEFAULT_TOAST_LIFE,
      ...message,
    });
  }, []);

  const showSuccessToast = useCallback(
    (detail: string, options?: ToastFeedbackOptions) => {
      showToast({
        severity: "success",
        summary: options?.summary ?? "Sucesso",
        detail,
        ...options,
      });
    },
    [showToast],
  );

  const showErrorToast = useCallback(
    (detail: string, options?: ToastFeedbackOptions) => {
      showToast({
        severity: "error",
        summary: options?.summary ?? "Erro",
        detail,
        ...options,
      });
    },
    [showToast],
  );

  const value = useMemo(
    () => ({
      showErrorToast,
      showSuccessToast,
      showToast,
    }),
    [showErrorToast, showSuccessToast, showToast],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toast
        ref={toastRef}
        position="top-right"
      />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error("useToast deve ser usado dentro de ToastProvider");
  }

  return context;
}
